import { useState } from "react";
import { Download, Eye, Printer } from "lucide-react";
import { Modal } from "#/components/common/Modal";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useDocumentAccessLog } from "../hooks";
import type { DocumentAccessAction, StoredDocument } from "../types";

function fmt(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-KE", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return value;
  }
}

const ACTION_ICON: Record<DocumentAccessAction, typeof Eye> = {
  VIEW: Eye,
  DOWNLOAD: Download,
  PRINT: Printer,
};

const ACTION_LABEL: Record<DocumentAccessAction, string> = {
  VIEW: "Viewed",
  DOWNLOAD: "Downloaded",
  PRINT: "Printed",
};

type Props = {
  document: StoredDocument | null;
  onClose: () => void;
};

/**
 * Who viewed/downloaded a document, and when — for HR_DOCUMENT_MANAGE / SUPER_ADMIN
 * to answer audit and compliance questions ("who looked at this employee's contract").
 */
export function DocumentAccessLogModal({ document, onClose }: Props) {
  const [page, setPage] = useState(0);
  const query = useDocumentAccessLog(document?.id, page, 10);

  return (
    <Modal
      open={!!document}
      title={document ? `Access log — ${document.originalFileName}` : "Access log"}
      onClose={onClose}
    >
      {query.isLoading && (
        <p className="py-6 text-center text-sm text-slate-500">Loading access log…</p>
      )}

      {query.isError && (
        <p className="py-6 text-center text-sm text-red-600">
          Could not load the access log for this document.
        </p>
      )}

      {query.data && (
        <>
          {document && (
            <p className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              <span>{document.viewCount ?? 0} view{(document.viewCount ?? 0) !== 1 ? "s" : ""}</span>
              <span>{document.downloadCount ?? 0} download{(document.downloadCount ?? 0) !== 1 ? "s" : ""}</span>
              {document.lastAccessedAt && <span>Last accessed {fmt(document.lastAccessedAt)}</span>}
            </p>
          )}

          {query.data.content.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">No access recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {query.data.content.map((entry) => {
                const Icon = ACTION_ICON[entry.action] ?? Eye;
                return (
                  <div
                    key={entry.id}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800">
                        {ACTION_LABEL[entry.action] ?? entry.action} by {entry.username}
                      </p>
                      <p className="text-xs text-slate-400">
                        {fmt(entry.accessedAt)}
                        {entry.ipAddress ? ` · ${entry.ipAddress}` : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4">
            <DataPagination
              page={query.data.number}
              totalPages={query.data.totalPages}
              totalElements={query.data.totalElements}
              size={query.data.size}
              onPageChange={setPage}
              onSizeChange={() => setPage(0)}
            />
          </div>
        </>
      )}
    </Modal>
  );
}
